import {
    ActionReducerMapBuilder,
    createAction,
} from '@reduxjs/toolkit'
import {NinchatEventCause} from 'api/types/eventCauses'
import {NinchatQueueUpdatedEvent} from 'api/types/events'
import type {UiState} from 'state/ui'
import {startAppListening} from 'middlewares/listener'

export const queuePickupDelayMilliseconds = 1000
export const notifyQueueAudienceRequest = createAction<NinchatQueueUpdatedEvent>('UI/notifyQueueAudienceRequest')
export const releaseQueuePickupDelay = createAction<NinchatQueueUpdatedEvent>('UI/releaseQueuePickupDelay')
export const startQueuePickupDelay = createAction<NinchatQueueUpdatedEvent>('UI/startQueuePickupDelay')

const queueUpdatedType = 'NINCHAT/event/queue_updated'
const queuePickupDelayTimers = new Map<NinchatQueueUpdatedEvent['queue_id'], ReturnType<typeof setTimeout>>()

const getPendingAudienceCount = (queueAttrs?: {
    length?: number
    unassigned_registered?: number
}): number => (queueAttrs?.length ?? 0) + (queueAttrs?.unassigned_registered ?? 0)

export const addQueuePickupDelayReducers = (builder: ActionReducerMapBuilder<UiState>) => builder
    .addCase(releaseQueuePickupDelay, (state, action) => {
        delete state.queue_pickup_delays?.[action.payload.queue_id]

        if (!Object.keys(state.queue_pickup_delays ?? {}).length) {
            delete state.queue_pickup_delays
        }
    })
    .addCase(startQueuePickupDelay, (state, action) => {
        state.queue_pickup_delays ??= {}
        state.queue_pickup_delays[action.payload.queue_id] = true
    })

startAppListening({
    effect: (action, listenerApi) => {
        const queueUpdate = action.payload as NinchatQueueUpdatedEvent

        if (queueUpdate.event_cause !== NinchatEventCause.NinchatAudienceRequestEventCause) {
            return
        }

        const originalState = listenerApi.getOriginalState()
        const originalQueue = originalState.ninchat.user_queues?.[queueUpdate.queue_id] ?? originalState.ninchat._queues?.[queueUpdate.queue_id]
        const isFirstAudienceRequest = getPendingAudienceCount(originalQueue?.queue_attrs) === 0 &&
            getPendingAudienceCount(queueUpdate.queue_attrs) > 0

        if (!isFirstAudienceRequest) {
            // Later arrivals must not release an earlier customer's pickup delay.
            listenerApi.dispatch(notifyQueueAudienceRequest(queueUpdate))
            return
        }

        listenerApi.dispatch(startQueuePickupDelay(queueUpdate))
        const timer = setTimeout(() => {
            // An earlier arrival still notifies, but cannot release a newer delay.
            if (queuePickupDelayTimers.get(queueUpdate.queue_id) === timer) {
                queuePickupDelayTimers.delete(queueUpdate.queue_id)
                listenerApi.dispatch(releaseQueuePickupDelay(queueUpdate))
            }

            listenerApi.dispatch(notifyQueueAudienceRequest(queueUpdate))
        }, queuePickupDelayMilliseconds)
        queuePickupDelayTimers.set(queueUpdate.queue_id, timer)
    },
    predicate: (action) => action.type === queueUpdatedType,
})
